import { useEffect, useRef, useState } from 'react'
import { datesBetween } from '@kronos/domain'
import type { LocalDate } from '@kronos/domain'

/**
 * Drag across days with a mouse to pick a run of them — the desktop way to
 * fill in a week. **Mouse only**: on a phone a finger moving over the grid is
 * a scroll, and stealing it would make the page impossible to move.
 *
 * Returns the range being dragged (for the preview) and the handlers a day
 * cell needs. A drag that ends where it began is an ordinary click, so the
 * click handler still runs; a real drag swallows the click that follows it.
 */
export function useDragSelect(onSelect: ((dates: readonly LocalDate[]) => void) | undefined) {
  const [range, setRange] = useState<{ from: LocalDate; to: LocalDate } | null>(null)
  const start = useRef<LocalDate | null>(null)
  const moved = useRef(false)
  const swallowClick = useRef(false)
  const latest = useRef(range)
  latest.current = range

  useEffect(() => {
    if (!onSelect) return
    const end = (): void => {
      const r = latest.current
      if (start.current && moved.current && r) {
        swallowClick.current = true
        onSelect(datesBetween(r.from, r.to))
        // The click that follows a drag lands (if at all) straight after this.
        setTimeout(() => (swallowClick.current = false), 0)
      }
      start.current = null
      moved.current = false
      setRange(null)
    }
    window.addEventListener('pointerup', end)
    return () => window.removeEventListener('pointerup', end)
  }, [onSelect])

  const handlers = (date: LocalDate) =>
    onSelect
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            if (e.pointerType !== 'mouse' || e.button !== 0 || e.shiftKey) return
            start.current = date
            moved.current = false
          },
          onPointerEnter: (e: React.PointerEvent) => {
            if (!start.current || (e.buttons & 1) === 0) return
            moved.current = moved.current || date !== start.current
            setRange({ from: start.current, to: date })
          },
        }
      : {}

  const inRange = (date: LocalDate): boolean =>
    range !== null && date >= (range.from < range.to ? range.from : range.to) && date <= (range.from < range.to ? range.to : range.from)

  return { handlers, inRange, swallowed: () => swallowClick.current }
}
